import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { IconButton, Flag, FlagSolid, IconSize, OverlayMode } from '@soteria/design-system-ve';
import { useState, useCallback, useEffect } from 'react';

import { MessageToast } from '../../../../core/components';
import { useSkipAuditItem } from '../../../hooks/useSkipAuditItem';

interface SkipButtonProps {
  item: GraphQLAuditItem;
  onSkipToggle?: (itemId: string, isSkipped: boolean) => void;
  isSessionCompleted?: boolean;
}

export function SkipButton({ item, onSkipToggle, isSessionCompleted = false }: SkipButtonProps) {
  // Use the optimistic skip hook for proper GraphQL mutations and error handling
  const { skipAuditItem, unskipAuditItem, errors } = useSkipAuditItem();
  const [isSkipped, setIsSkipped] = useState(item.status === 'skipped');
  const [dismissedErrorIds, setDismissedErrorIds] = useState<Set<string>>(new Set());

  // Handle toast dismissal for a specific error
  const handleToastDismiss = useCallback((errorId: string) => {
    setDismissedErrorIds(prev => new Set([...prev, errorId]));
  }, []);

  // Clear dismissed error IDs when errors clear (new operation starts)
  useEffect(() => {
    if (errors.length === 0) {
      setDismissedErrorIds(new Set());
    }
  }, [errors.length]);

  // Filter errors to show only non-dismissed ones
  const visibleErrors = errors.filter(error => {
    const errorId = `${error.code}-${error.correlationId || 'no-correlation'}`;

    return !dismissedErrorIds.has(errorId);
  });

  // Sync internal state with prop changes
  useEffect(() => {
    setIsSkipped(item.status === 'skipped');
  }, [item.status]);

  // Handle flag toggle with optimistic updates
  const handleFlagToggle = useCallback(async () => {
    // Capture the original skip state for rollback
    const previousSkipState = isSkipped;
    // Update local state immediately for UI responsiveness (optimistic)
    const newSkipStatus = !isSkipped;

    setIsSkipped(newSkipStatus);
    onSkipToggle?.(item.id, newSkipStatus);

    try {
      let result;

      if (previousSkipState) {
        // Unskip the item
        result = await unskipAuditItem({
          input: {
            itemId: item.id,
          },
        });
      } else {
        // Skip the item
        result = await skipAuditItem({
          input: {
            itemId: item.id,
          },
        });
      }

      // Check if the operation failed and rollback if needed
      if (!result.success) {
        setIsSkipped(previousSkipState);
        onSkipToggle?.(item.id, previousSkipState);
      }
    } catch (error) {
      // Rollback the local state on error (errors are handled by hook's error state)
      console.log('Skip operation threw error in SkipButton, rolling back:', error);
      setIsSkipped(previousSkipState);
      onSkipToggle?.(item.id, previousSkipState);
    }
  }, [isSkipped, item.id, skipAuditItem, unskipAuditItem, onSkipToggle]);

  // Handle skip toggle with event propagation prevention
  const handleSkipToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card selection
      handleFlagToggle();
      // Remove focus from the button after clicking
      (e.currentTarget as HTMLButtonElement).blur();
    },
    [handleFlagToggle]
  );

  return (
    <div>
      {/* Show error toasts for all non-dismissed errors */}
      {visibleErrors.map(error => {
        const errorId = `${error.code}-${error.correlationId || 'no-correlation'}`;

        return (
          <MessageToast
            key={errorId}
            isVisible
            ariaLabel={`Skip operation error: ${error.userMessage}`}
            message={error.userMessage}
            subMessage={`Error Code: ${error.code}${error.correlationId ? `\nCorrelation ID: ${error.correlationId}` : ''}`}
            variant="error"
            onClose={() => handleToastDismiss(errorId)}
          />
        );
      })}

      <IconButton
        data-skipped={isSkipped}
        disabled={isSessionCompleted}
        icon={isSkipped ? FlagSolid : Flag}
        iconSize={IconSize.XSmall}
        overlayMode={OverlayMode.Solid}
        title={isSessionCompleted ? 'Session completed - read only' : isSkipped ? 'Remove skip' : 'Skip this item'}
        onClick={handleSkipToggle}
      />
    </div>
  );
}
